import type { InputHTMLAttributes } from "react";
import React, { useCallback, useMemo } from "react";
import { Input } from "reactstrap";
import cx from "classnames";
import { uniqueId } from "lodash-es";

import { Icon } from "../../Icon/Icon";
import styles from "./Checkbox.module.scss";

interface CheckboxProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "onChange" | "type" | "checked"
> {
  checked?: boolean;
  onChange?: () => void;
  wrapperClassName?: string;
  classNames?: string;
  classNamesIcon?: string;
  disabled?: boolean;
  id?: string;
}

export const Checkbox: React.FC<CheckboxProps> = React.memo(
  ({
    checked = false,
    onChange = () => {},
    wrapperClassName,
    classNames,
    classNamesIcon,
    disabled = false,
    id,
    ...restProps
  }) => {
    const handleClick = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (!disabled) {
          e.stopPropagation();
          onChange();
        }
      },
      [onChange, disabled],
    );

    const checkboxId = useMemo(() => uniqueId("checkbox-"), []);

    return (
      <>
        <div
          className={cx(
            "position-relative d-inline-block icon-18",
            wrapperClassName,
            {
              "opacity-50 cursor-not-allowed": disabled,
            },
          )}
          onClick={handleClick}
        >
          <Input
            id={id ?? checkboxId}
            className="mt-0"
            type="checkbox"
            checked={checked}
            onChange={onChange}
            disabled={disabled}
            {...restProps}
          />
          <div
            className={cx(
              styles.checkbox,
              "position-absolute top-0 start-0 end-0 bottom-0",
              "d-flex justify-content-center align-items-center",
              {
                "bg-blue-300 border-info": checked,
              },
              classNames,
            )}
          >
            {checked && (
              <Icon
                prefix="trox"
                iconName="check-regular"
                style={{ transform: "rotate(0)" }}
                className={cx("icon-14 text-white", classNamesIcon)}
              />
            )}
          </div>
        </div>
      </>
    );
  },
);

Checkbox.displayName = "Checkbox";
